import {
  LayoutDashboard,
  Server,
  Network,
  Boxes,
  ShieldHalf,
  Scale,
  ShieldCheck,
  Lock,
  KeyRound,
  HardDrive,
  Camera,
  Archive,
  Gauge,
  Receipt,
  Activity,
  ArrowLeftRight,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import type { ZoneFeatureKey } from '@/api/opennebula';

export interface ComputeNavEntry {
  label: string;
  /** Path segment under `/services/compute/:serviceId` ('' = console root). */
  slug: string;
  icon: LucideIcon;
  /** Hidden when the service's zone disables this feature (default: always shown). */
  feature?: ZoneFeatureKey;
}

export interface ComputeNavGroup {
  /** Optional uppercase section heading. */
  heading?: string;
  items: ComputeNavEntry[];
}

/**
 * Compute (OpenNebula) console navigation, grouped to mirror the production portal
 * (Dashboard / Instances · Networking · Storage · Access & usage). Slugs map 1:1 to
 * the nested routes in ComputeConsole. Rendered in the app's `ContextualSidebar`
 * (scope-driven), not a secondary rail inside the content.
 */
export const COMPUTE_NAV_GROUPS: ComputeNavGroup[] = [
  {
    items: [
      { label: 'Dashboard', slug: '', icon: LayoutDashboard },
      { label: 'Instances', slug: 'instances', icon: Server },
    ],
  },
  {
    heading: 'Networking',
    items: [
      { label: 'Networks', slug: 'networks', icon: Network, feature: 'networking' },
      { label: 'VPC', slug: 'vpc', icon: Boxes, feature: 'vpc' },
      { label: 'Elastic IP & VPN', slug: 'vpn', icon: ShieldHalf, feature: 'elastic_ip' },
      { label: 'Load Balancers', slug: 'load-balancers', icon: Scale, feature: 'load_balancer' },
      { label: 'Security Groups', slug: 'security-groups', icon: ShieldCheck, feature: 'security_group' },
      { label: 'Certificates', slug: 'certificates', icon: Lock, feature: 'load_balancer' },
    ],
  },
  {
    heading: 'Storage',
    items: [
      { label: 'Volumes', slug: 'volumes', icon: HardDrive, feature: 'volume' },
      { label: 'Snapshots', slug: 'snapshots', icon: Camera, feature: 'snapshot' },
      { label: 'Backups', slug: 'backups', icon: Archive, feature: 'backup' },
    ],
  },
  {
    heading: 'Access & usage',
    items: [
      { label: 'SSH Keys', slug: 'ssh-keys', icon: KeyRound, feature: 'ssh_key' },
      { label: 'Quotas & Usage', slug: 'quotas', icon: Gauge },
      { label: 'Business Usage', slug: 'business', icon: Receipt },
    ],
  },
  {
    items: [
      { label: 'Settings', slug: 'settings', icon: Settings },
    ],
  },
];

/**
 * Instance-scope sidebar entries — the detail page's sub-sections, shown in the
 * ContextualSidebar while inside `/instances/:instanceId/*` (mirror the nested
 * routes in InstanceDetail; `resize` is a sub-flow, not a nav entry).
 */
export const INSTANCE_NAV: ComputeNavEntry[] = [
  { label: 'Overview', slug: '', icon: LayoutDashboard },
  { label: 'Monitoring', slug: 'monitoring', icon: Activity },
  { label: 'Network', slug: 'network', icon: Network },
  { label: 'Network traffic', slug: 'traffic', icon: ArrowLeftRight },
  { label: 'Volumes', slug: 'volumes', icon: HardDrive },
  { label: 'Snapshots', slug: 'snapshots', icon: Camera },
  { label: 'Backups', slug: 'backups', icon: Archive },
];

/** Absolute href for a nav entry under the console base (`''` slug = base/root). */
export function computeHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/**
 * The active entry href for the current pathname — the longest matching entry so
 * `/instances/:id/...` highlights Instances and the bare base highlights Dashboard.
 */
export function computeActiveId(base: string, pathname: string): string {
  const all = COMPUTE_NAV_GROUPS.flatMap((g) => g.items);
  const matches = all
    .map((e) => computeHref(base, e.slug))
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
